import { Divider, Stack, Typography, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import FormButton from '../FormButton';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LockIcon from '@mui/icons-material/Lock';
import { useNavigate, useParams } from 'react-router-dom';
import { PATHS } from '../../../constants/routes';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';

interface IBaseFormProps {
  children: JSX.Element;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onReset?: () => void;
}

declare global {
  interface Window {
    ymGlobal: any;
  }
}

const BaseForm: React.FC<IBaseFormProps> = ({ children, onSubmit, onReset }) => {
  const { url } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.user);
  const { isGenerationLoading, isHistoryLoading } = useSelector((state: RootState) => state.generation);
  const totalFreeGenerations = user?.free_tier ? user.free_tier.total_generations : 0;
  const freeAvailableGenerations = user?.free_tier ? 
        ( user.free_tier?.total_generations - user.free_tier?.used_generations ) : 0;
  // const isActiveSubscription = !!user?.subscriptions;
  const creditsLeft = user ? user?.credits.total - user?.credits.used : 0;

  const handleSelectPlan = () => {
    // window.open('https://www.copymonkey.app/beta', '_blank', 'noopener,noreferrer');

    if (window.ymGlobal) {
      window.ymGlobal(90338500, 'reachGoal', 'select_plan_button');
    }

    navigate(PATHS.billing);
  };

  return (
    <Stack
      component={'form'}
      sx={{
        boxSizing: 'border-box',
        justifyContent: 'space-between',
        flexGrow: 1,
      }}
    >
      <Box
        p={{
          mobile: '0 10px',
          tablet: '0 20px',
          laptop: '0 30px',
          desktop: '0 40px',
        }}
      >
        {children}
      </Box>

      <Stack gap={'20px'}>
        <Box
          sx={{
            boxSizing: 'border-box',
            padding: {
              mobile: '0 10px',
              tablet: '0 20px',
              laptop: '0 30px',
              desktop: '0 40px',
            },
          }}
        >
          {creditsLeft <= 0 && user?.free_tier && (
            <Stack
              sx={{
                backgroundColor: theme.custom.palette.primary.background,
                borderRadius: '8px',
                p: '20px',
                border: `1px solid ${theme.custom.palette.primary.lightgray}`,
              }}
              gap={'20px'}
            >
              <Typography align="center">
                У вас есть {totalFreeGenerations} бесплатных генераций в неделю. Осталось {freeAvailableGenerations}
              </Typography>

              {freeAvailableGenerations <= 0 && (
                <Stack alignItems={'center'} gap={'8px'}>
                  <Typography align="center" variant={'h3'}>
                    Чтобы продолжить пользоваться, выберите пакет генераций
                  </Typography>
                  <FormButton onClick={handleSelectPlan} size={'s'} variant={'green'}>
                    Купить генерации
                  </FormButton>
                </Stack>
              )}
            </Stack>
          )}
        </Box>

        <Divider light />
        <Stack
          direction={{
            mobile: 'column',
            tablet: 'row',
          }}
          justifyContent={'space-between'}
          gap={{
            mobile: '15px',
            tablet: 0,
          }}
          p={{
            mobile: '20px 10px 0',
            tablet: '20px 20px 0',
            laptop: '20px 30px 0',
            desktop: '20px 40px 0',
          }}
        >
          <FormButton
            variant={'secondary'}
            startIcon={<CloseRoundedIcon />}
            onClick={onReset}
            type={'secondary'}
            disabled={isGenerationLoading || isHistoryLoading}
          >
            {url === 'chat' ? 'Новый диалог' : 'Очистить форму'}
          </FormButton>

          <FormButton
            endIcon={creditsLeft <= 0 && freeAvailableGenerations <= 0 ? <LockIcon /> : <ArrowForwardRoundedIcon />}
            isLoading={isGenerationLoading}
            onClick={onSubmit}
            disabled={creditsLeft <= 0 && freeAvailableGenerations <= 0 || isHistoryLoading}
          >
            {url === 'chat' ? 'Отправить' : 'Сгенерировать'}
          </FormButton>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default BaseForm;
